/**
 * <quick-view>
 *
 * Body of the "Quick View" template section (sections/quick-view.liquid).
 * Two jobs:
 *
 * 1. Put a Quick View trigger on every product card on the page. Cards are
 *    recognised by CARD_SELECTORS (the Vue PLP / search grid, Searchspring
 *    Snap recommendation cards, the Liquid product card); the trigger is
 *    cloned from the section's <template> into the card's image wrapper
 *    (HOST_SELECTORS, else the product link's parent). A MutationObserver
 *    keeps up with cards that arrive later (filters, infinite scroll, Snap
 *    carousels). Nothing is stored per card: the product handle and the
 *    `?variant=` of the card's product link are read at click time, so the
 *    swatch a shopper picked on the card carries over.
 *
 * 2. Open a native <dialog> and fill it with the product's panel, rendered
 *    by Liquid: sections/quick-view-product.liquid fetched with the Section
 *    Rendering API on the product URL. Picking an option resolves the
 *    variant from the JSON the panel embeds and re-fetches the panel with
 *    `?variant=`, so price, availability, media and the PDP link always
 *    come from the server. Sibling-product colors (products stitched by a
 *    `Sibling:<id>` tag, as on the PDP) are loaded from the Storefront API
 *    and sorted in with the color swatches in the family order; picking one
 *    loads that product. The star rating is refreshed from Yotpo, the
 *    source the PDP's widget reads.
 *    Add to Cart posts the panel's form to /cart/add.js and then refreshes
 *    and opens the existing mini cart.
 *    Opening shows the backdrop at once and slides the panel in when its
 *    content is ready (on desktop sibling colours included), within
 *    CONTENT_WAIT_MS;
 *    hovering, pressing or focusing a trigger prefetches that panel.
 *
 * Enabling:
 * - `data-mode="on"` enables the element itself with `data-layout`;
 * - `data-mode="ab_test"` waits for an A/B tool to set the `enabled`
 *   attribute to the desktop layout:
 *     document.querySelector('quick-view').setAttribute('enabled', 'slide-out')
 *   (or 'modal'). Removing the attribute removes every trigger and closes
 *   an open panel.
 *
 * Events (bubbling, on the element): quick-view:open, quick-view:close,
 * quick-view:added ({ variantId, quantity }).
 */

/** Roots of the product cards the trigger is added to */
const CARD_SELECTORS = ['.collection-grid-item', '.ss__result', 'article.pi'];

/** Containers whose cards get no trigger (the Searchspring search dropdown) */
const EXCLUDE_SELECTORS = ['.ss__autocomplete'];

/** Where inside a card the trigger goes; the product link's parent otherwise */
const HOST_SELECTORS = ['.collection-grid-item__link-wrapper', '.pi__img'];

const PRODUCT_LINK = 'a[href*="/products/"]';

/** Must match the closing transition on `.quick-view__panel` (--quick-view-duration) */
const HIDE_DURATION_MS = 250;

/**
 * How long open() holds the panel back while the product loads, so it
 * slides in with its content instead of a skeleton that is swapped (and, on
 * the content-height mobile sheet, resized) mid-slide. The backdrop shows
 * at once. Desktop panels have a fixed size, so they wait less. The mobile
 * sheet waits for the product only; past the budget the skeleton goes up and
 * the product replaces it once the slide is over.
 */
const CONTENT_WAIT_MS = { desktop: 250, mobile: 500 };

/** Same breakpoint as the mobile sheet in the section's stylesheet */
const MOBILE_QUERY = '(max-width: 767px)';

/** Panels fetched on hover / press of a trigger are used by the next open within this window */
const PREFETCH_TTL_MS = 30000;

/** Mobile sheet drag: share of its height, or speed (px/ms), that closes it on release */
const DRAG_CLOSE_RATIO = 0.25;
const DRAG_CLOSE_VELOCITY = 0.5;

/** Longest wait for the lead image to decode before the panel slides in */
const REVEAL_DECODE_MS = 150;

/** Height change of a sheet on screen, animated instead of jumping (--quick-view-ease-open) */
const SETTLE_ANIMATION = { duration: 300, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' };

/**
 * Yotpo's public bottomline endpoint, the numbers the PDP's Yotpo widget
 * shows (the colour family's total); {key} = theme setting yotpo_appkey
 */
const YOTPO_BOTTOMLINE_URL = 'https://api-cdn.yotpo.com/products/{key}/{id}/bottomline';

/** Same version as lib/get-product-siblings.js, which the PDP uses with this token */
const STOREFRONT_API_VERSION = '2022-07';

const SIBLINGS_QUERY = `
  query QuickViewSiblings($query: String!) {
    products(first: 50, query: $query) {
      edges {
        node {
          handle
          title
          tags
          availableForSale
          featuredImage { url altText }
          images(first: 250) {
            edges {
              node { url altText }
            }
          }
          variants(first: 100) {
            edges {
              node {
                id
                availableForSale
                image { url altText }
                selectedOptions { name value }
              }
            }
          }
        }
      }
    }
  }
`;

/** On a sibling swatch's stand-in, rendered by Liquid until the siblings load */
const PLACEHOLDER_ATTR = 'data-quick-view-placeholder';
/** On the pickup line and the rating row while they load: room kept, line invisible */
const PENDING_ATTR = 'data-quick-view-pending';

/** Set on <body> by the mini cart while it is open */
const SCROLL_LOCK_CLASS = 'no-scroll';

/** On the dialog: backdrop shown */
const OPEN_CLASS = 'is-open';
/** On the dialog: panel slid in */
const REVEALED_CLASS = 'is-revealed';
/** On the dialog: the mobile sheet follows the finger, no transition */
const DRAGGING_CLASS = 'is-dragging';
const BODY_OPEN_CLASS = 'quick-view-open';

const REFS = {
  triggerTemplate: 'template[data-quick-view-trigger]',
  skeletonTemplate: 'template[data-quick-view-skeleton]',
  dialog: '[data-quick-view-dialog]',
  panel: '[data-quick-view-panel]',
  body: '[data-quick-view-body]',
  close: '[data-quick-view-close]',
  header: '[data-quick-view-header]',
  badgeSlot: '[data-quick-view-badge-slot]',
};

/** Hooks inside the fetched panel */
const HOOKS = {
  trigger: '[data-quick-view-trigger-btn]',
  product: '[data-quick-view-product]',
  title: '#quick-view-title',
  badge: '[data-quick-view-badge]',
  form: '[data-quick-view-form]',
  variantId: '[data-quick-view-variant-id]',
  option: 'input[data-quick-view-option]',
  variants: 'script[data-quick-view-variants]',
  siblings: '[data-quick-view-siblings]',
  siblingGroup: '[data-quick-view-sibling-group]',
  sibling: '[data-quick-view-sibling]',
  qtyStep: '[data-quick-view-qty-step]',
  qtyInput: '[data-quick-view-qty-input]',
  add: '[data-quick-view-add]',
  addText: '[data-quick-view-add-text]',
  message: '[data-quick-view-message]',
  klarna: '[data-quick-view-klarna]',
  wishlist: '[data-quick-view-wishlist]',
  pickup: '[data-quick-view-pickup]',
  pickupStores: '[data-quick-view-pickup-stores]',
  pickupData: 'script[data-quick-view-pickup-data]',
  rating: '[data-quick-view-rating]',
  ratingStars: '[data-quick-view-rating-stars]',
  ratingCount: '[data-quick-view-rating-count]',
};

class QuickView extends HTMLElement {
  static observedAttributes = ['enabled'];

  /** @type {AbortController | null} */
  #abort = null;

  /** @type {MutationObserver | null} */
  #observer = null;

  #injectScheduled = false;

  /** @type {Set<HTMLElement>} */
  #triggers = new Set();

  /** @type {AbortController | null} */
  #fetchAbort = null;

  /** @type {Map<string, Promise<object[]>>} */
  #siblingsCache = new Map();

  /** A render is waiting for Wishlist King's `wishlist-theme-hook-ready` */
  #wishlistPending = false;

  /** @type {Map<string, Promise<string[]>>} store names per variant id */
  #pickupCache = new Map();

  /** @type {Map<string, Promise<{ average: number, total: number } | null>>} per product id */
  #ratingCache = new Map();

  /** Sibling products of the panel currently shown (Storefront nodes) */
  #siblings = [];

  /** @type {HTMLElement | null} */
  #opener = null;

  /** @type {string | null} */
  #handle = null;

  /** @type {number | undefined} */
  #closeTimer;

  /** Bumped by every open(), so a slow open does not reveal over a newer one */
  #openToken = 0;

  /**
   * Settles when the panel's open slide is over. Content that changes the
   * mobile sheet's height waits for it: mid-slide, the bottom-anchored sheet
   * would jump by the difference; after it, #settleHeight animates the change.
   * @type {Promise<void>}
   */
  #slideDone = Promise.resolve();

  /** @type {Map<string, { request: Promise<HTMLElement>, time: number }>} */
  #prefetched = new Map();

  /** @type {{ startY: number, startTime: number, offset: number } | null} */
  #drag = null;

  /**
   * @type {{
   *   dialog: HTMLDialogElement | null,
   *   panel: HTMLElement | null,
   *   body: HTMLElement | null,
   *   badgeSlot: HTMLElement | null,
   *   triggerTemplate: HTMLTemplateElement | null,
   *   skeletonTemplate: HTMLTemplateElement | null,
   * }}
   */
  #refs = {
    dialog: null,
    panel: null,
    body: null,
    badgeSlot: null,
    triggerTemplate: null,
    skeletonTemplate: null,
  };

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#refs = {
      dialog: this.querySelector(REFS.dialog),
      panel: this.querySelector(REFS.panel),
      body: this.querySelector(REFS.body),
      badgeSlot: this.querySelector(REFS.badgeSlot),
      triggerTemplate: this.querySelector(REFS.triggerTemplate),
      skeletonTemplate: this.querySelector(REFS.skeletonTemplate),
    };

    const { dialog, body, triggerTemplate } = this.#refs;
    if (!dialog || !body || !triggerTemplate || typeof dialog.showModal !== 'function') {
      console.warn('[quick-view] dialog or trigger template missing; the component stays inert.');
      return;
    }

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) this.close();
    }, { signal });
    // Browsers without `overflow: clip` keep a scrollable dialog: undo any
    // scroll (focus on the off-screen panel would otherwise show it early).
    dialog.addEventListener('scroll', () => {
      dialog.scrollTop = 0;
      dialog.scrollLeft = 0;
    }, { signal, passive: true });
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close();
    }, { signal });
    this.querySelector(REFS.close)?.addEventListener('click', () => this.close(), { signal });

    const header = this.querySelector(REFS.header);
    header?.addEventListener('pointerdown', (event) => this.#onDragStart(event), { signal });
    header?.addEventListener('pointermove', (event) => this.#onDragMove(event), { signal });
    header?.addEventListener('pointerup', (event) => this.#onDragEnd(event), { signal });
    header?.addEventListener('pointercancel', (event) => this.#onDragEnd(event, { cancelled: true }), { signal });

    document.addEventListener('click', (event) => this.#onDocumentClick(event), { signal, capture: true });
    // Intent to open: hover (desktop), press (touch fires it ~100ms before
    // the click) or keyboard focus starts fetching the panel early.
    const prefetch = (event) => this.#onTriggerIntent(event);
    document.addEventListener('pointerover', prefetch, { signal, passive: true });
    document.addEventListener('pointerdown', prefetch, { signal, passive: true });
    document.addEventListener('focusin', prefetch, { signal });
    body.addEventListener('change', (event) => this.#onChange(event), { signal });
    body.addEventListener('click', (event) => this.#onBodyClick(event), { signal });
    body.addEventListener('submit', (event) => this.#onSubmit(event), { signal });

    if (this.dataset.mode === 'on') {
      this.enabled = this.dataset.layout || 'slide-out';
    } else {
      this.#sync();
    }
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
    this.#fetchAbort?.abort();
    this.#fetchAbort = null;
    this.#stopObserving();
    this.#removeTriggers();
    clearTimeout(this.#closeTimer);
    this.#prefetched.clear();
    this.#drag = null;
    if (this.#refs.dialog?.open) this.#refs.dialog.close();
    document.body.classList.remove(BODY_OPEN_CLASS);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue) this.#sync();
  }

  /** @returns {boolean} */
  get enabled() {
    return this.hasAttribute('enabled');
  }

  /** @param {string | boolean} value - layout name to enable, falsy to disable */
  set enabled(value) {
    if (value) {
      this.setAttribute('enabled', typeof value === 'string' ? value : '');
    } else {
      this.removeAttribute('enabled');
    }
  }

  /** @returns {'slide-out' | 'modal'} */
  get layout() {
    const value = this.getAttribute('enabled');
    if (value === 'modal' || value === 'slide-out') return value;
    return this.dataset.layout === 'modal' ? 'modal' : 'slide-out';
  }

  /**
   * Open the panel for a product.
   * @param {{ handle: string, variantId?: string | null, opener?: HTMLElement | null }} options
   */
  async open({ handle, variantId = null, opener = null }) {
    const { dialog, panel } = this.#refs;
    if (!dialog || !handle) return;

    this.#openToken += 1;
    const token = this.#openToken;
    this.#opener = opener;
    this.#handle = handle;
    clearTimeout(this.#closeTimer);
    dialog.dataset.layout = this.layout;

    if (!dialog.open) {
      dialog.showModal();
      // Force a style pass on the closed state so the open transition runs.
      dialog.getBoundingClientRect();
    }
    // Added unconditionally: a reopen during the close transition finds the
    // dialog still open but with the classes already removed by close().
    document.body.classList.add(BODY_OPEN_CLASS);
    dialog.classList.add(OPEN_CLASS);

    // The panel is still off-screen: the skeleton only shows if the product
    // is not in by the time it slides in.
    this.#showSkeleton();
    if (panel) panel.scrollTop = 0;

    let onRender;
    const rendered = new Promise((resolve) => { onRender = resolve; });
    const loading = this.#load(handle, variantId, {
      focus: HOOKS.title,
      request: this.#takePrefetched(handle, variantId),
      onRender,
    });
    // Desktop panels have a fixed size and wait briefly for the sibling
    // colours and the pickup line too. The mobile sheet goes up as soon as
    // the product is in: those two are separate requests (the pickup one
    // took up to 840ms) and land once the slide is over.
    const mobile = window.matchMedia(MOBILE_QUERY).matches;
    const ready = mobile ? Promise.race([rendered, loading]) : loading;
    const budget = mobile ? CONTENT_WAIT_MS.mobile : CONTENT_WAIT_MS.desktop;
    await Promise.race([ready, QuickView.#wait(budget)]);
    if (token !== this.#openToken || !dialog.classList.contains(OPEN_CLASS)) return;

    // Let the new content be painted, and the lead image decoded, while the
    // panel is still off-screen. Whatever reacts to the DOM change (layout,
    // raster, and third-party mutation observers such as Microsoft Clarity,
    // which spent 350ms+ per panel insert) then runs before the slide
    // starts instead of stalling its first frames.
    const lead = this.#refs.body?.querySelector(`${HOOKS.wishlist} img`); // first image = the lead
    await Promise.all([
      QuickView.#nextFrames(2),
      Promise.race([lead?.decode?.().catch(() => {}), QuickView.#wait(REVEAL_DECODE_MS)]),
    ]);
    if (token !== this.#openToken || !dialog.classList.contains(OPEN_CLASS)) return;

    dialog.getBoundingClientRect();
    dialog.classList.add(REVEALED_CLASS);
    // Only the content-height sheet moves when its content changes.
    this.#slideDone = mobile ? QuickView.#wait(this.#openDuration()) : Promise.resolve();
    await loading;
  }

  /** Length of the panel's open slide in ms (--quick-view-open-duration, 0 with reduced motion) */
  #openDuration() {
    const { panel } = this.#refs;
    if (!panel) return 0;
    const durations = getComputedStyle(panel).transitionDuration.split(',').map((value) => parseFloat(value) || 0);
    return Math.max(0, ...durations) * 1000;
  }

  /** @param {number} ms */
  static #wait(ms) {
    return new Promise((resolve) => { setTimeout(resolve, ms); });
  }

  /**
   * Resolves after `count` rendered frames; a timeout stands in for frames
   * where requestAnimationFrame does not run (background tabs).
   * @param {number} count
   */
  static #nextFrames(count) {
    const frames = new Promise((resolve) => {
      const step = (left) => (left ? requestAnimationFrame(() => step(left - 1)) : resolve());
      step(count);
    });
    return Promise.race([frames, QuickView.#wait(count * 50)]);
  }

  /**
   * @param {{ restoreFocus?: boolean }} [options] - restoreFocus false hands
   *   focus to the open mini cart instead of the trigger (after an add, the
   *   cart is the dialog the shopper is now in). dialog.close() itself moves
   *   focus back to the trigger, so the hand-over happens right after it.
   */
  close({ restoreFocus = true } = {}) {
    const { dialog } = this.#refs;
    if (!dialog?.open) return;

    this.#fetchAbort?.abort();
    this.#drag = null;
    // A drag offset left inline is where the closing slide starts from.
    if (this.#refs.panel) this.#refs.panel.style.transform = '';
    dialog.classList.remove(OPEN_CLASS, REVEALED_CLASS, DRAGGING_CLASS);
    clearTimeout(this.#closeTimer);
    this.#closeTimer = setTimeout(() => {
      if (dialog.open) dialog.close();
      document.body.classList.remove(BODY_OPEN_CLASS);
      const cart = restoreFocus ? null : QuickView.#openMiniCartFocusTarget();
      if (cart) cart.focus();
      else this.#opener?.focus?.();
      this.#opener = null;
      this.dispatchEvent(new CustomEvent('quick-view:close', { bubbles: true }));
    }, HIDE_DURATION_MS);
  }

  /**
   * First focusable control of the mini cart drawer while it is open
   * (lib/mini-cart.js toggles `.mini-cart.open`), or null.
   * @returns {HTMLElement | null}
   */
  static #openMiniCartFocusTarget() {
    const drawer = document.querySelector('mini-cart .mini-cart.open');
    const target = drawer?.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    return target instanceof HTMLElement ? target : null;
  }

  /* ------------------------------------------------------------------ */
  /* Enabling and trigger injection                                       */
  /* ------------------------------------------------------------------ */

  #sync() {
    if (!this.isConnected || !this.#refs.dialog) return;

    if (this.enabled) {
      this.#refs.dialog.dataset.layout = this.layout;
      this.#startObserving();
      this.#inject(document.body);
      return;
    }

    this.#stopObserving();
    this.#removeTriggers();
    this.close();
  }

  #startObserving() {
    if (this.#observer) return;
    this.#observer = new MutationObserver((mutations) => {
      let relevant = false;
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE && !this.contains(node)) {
            relevant = true;
            break;
          }
        }
        if (relevant) break;
      }
      if (relevant) this.#scheduleInject();
    });
    this.#observer.observe(document.body, { childList: true, subtree: true });
  }

  #stopObserving() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  #scheduleInject() {
    if (this.#injectScheduled) return;
    this.#injectScheduled = true;
    // A timeout, not requestAnimationFrame: cards rendered while the tab is
    // in the background must get their trigger too.
    setTimeout(() => {
      this.#injectScheduled = false;
      if (this.enabled) this.#inject(document.body);
    }, 0);
  }

  /**
   * Add a trigger to every recognised card under `root` that has none.
   * @param {ParentNode} root
   */
  #inject(root) {
    if (!this.#refs.triggerTemplate) return;
    for (const trigger of this.#triggers) {
      if (!trigger.isConnected) this.#triggers.delete(trigger);
    }
    const cards = root.querySelectorAll(CARD_SELECTORS.join(','));
    for (const card of cards) this.#injectCard(card);
  }

  /**
   * Add a trigger to one card, unless it has one or is not a product card.
   * @param {Element} card
   */
  #injectCard(card) {
    if (this.contains(card) || card.closest(EXCLUDE_SELECTORS.join(','))) return;

    const existing = card.querySelector(HOOKS.trigger);
    if (existing) {
      this.#triggers.add(existing);
      return;
    }

    const link = card.querySelector(PRODUCT_LINK);
    if (!link) return;

    const host = HOST_SELECTORS.map((selector) => card.querySelector(selector)).find(Boolean)
      || link.parentElement;
    const trigger = this.#refs.triggerTemplate?.content.firstElementChild?.cloneNode(true);
    if (!host || !(trigger instanceof HTMLElement)) return;

    const label = this.dataset.triggerLabel || 'Quick View';
    const title = card.querySelector('h1, h2, h3, h4, h5, h6')?.textContent.trim()
      || card.querySelector('img')?.getAttribute('alt')?.trim()
      || link.textContent.trim();
    trigger.setAttribute('aria-label', title ? `${label}: ${title}` : label);

    host.setAttribute('data-quick-view-host', '');
    host.setAttribute('data-quick-view-mobile', this.dataset.mobileTrigger || 'icon');
    // The trigger is positioned against the host; card styles from other
    // systems (Snap results) can reset it to static, so pin it inline.
    if (getComputedStyle(host).position === 'static') {
      host.style.position = 'relative';
      host.setAttribute('data-quick-view-positioned', '');
    }
    host.append(trigger);
    this.#triggers.add(trigger);
  }

  #removeTriggers() {
    for (const trigger of this.#triggers) {
      const host = trigger.parentElement;
      trigger.remove();
      if (host && !host.querySelector(HOOKS.trigger)) {
        host.removeAttribute('data-quick-view-host');
        host.removeAttribute('data-quick-view-mobile');
        if (host.hasAttribute('data-quick-view-positioned')) {
          host.style.position = '';
          host.removeAttribute('data-quick-view-positioned');
        }
      }
    }
    this.#triggers.clear();
  }

  /** @param {MouseEvent} event */
  #onDocumentClick(event) {
    if (!this.enabled || !(event.target instanceof Element)) return;
    const trigger = event.target.closest(HOOKS.trigger);
    if (!trigger || this.contains(trigger)) return;

    event.preventDefault();
    event.stopPropagation();

    const target = QuickView.#targetOf(trigger);
    if (!target) {
      console.warn('[quick-view] no product link found for this card.');
      return;
    }
    this.open({ ...target, opener: trigger });
  }

  /** @param {Event} event - pointerover / pointerdown / focusin anywhere */
  #onTriggerIntent(event) {
    if (!this.enabled || !(event.target instanceof Element)) return;
    const trigger = event.target.closest(HOOKS.trigger);
    if (!trigger || this.contains(trigger)) return;
    const target = QuickView.#targetOf(trigger);
    if (target) this.#prefetch(target.handle, target.variantId);
  }

  /**
   * Product handle and variant the card of a trigger links to.
   * @param {Element} trigger
   * @returns {{ handle: string, variantId: string | null } | null}
   */
  static #targetOf(trigger) {
    const link = trigger.closest(CARD_SELECTORS.join(','))?.querySelector(PRODUCT_LINK);
    return link ? QuickView.#parseProductLink(link) : null;
  }

  /**
   * Start fetching a panel ahead of the click. One request per product and
   * variant within PREFETCH_TTL_MS; a failed one is forgotten so the click
   * fetches again.
   * @param {string} handle
   * @param {string | null} variantId
   */
  #prefetch(handle, variantId) {
    const key = `${handle}|${variantId || ''}`;
    const entry = this.#prefetched.get(key);
    if (entry && Date.now() - entry.time < PREFETCH_TTL_MS) return;

    const request = this.#fetchSection(handle, variantId);
    request
      .then((product) => {
        // Warm the sibling colours, store stock and rating too: the panel waits for them.
        const tag = product.dataset.siblingTag;
        if (tag) this.#fetchSiblings(tag);
        this.#pickupStores(product);
        this.#ratingOf(product);
      })
      .catch(() => {
        if (this.#prefetched.get(key)?.request === request) this.#prefetched.delete(key);
      });
    this.#prefetched.set(key, { request, time: Date.now() });
  }

  /**
   * The prefetched panel for this product and variant, used once.
   * @param {string} handle
   * @param {string | null} variantId
   * @returns {Promise<HTMLElement> | null}
   */
  #takePrefetched(handle, variantId) {
    const key = `${handle}|${variantId || ''}`;
    const entry = this.#prefetched.get(key);
    this.#prefetched.delete(key);
    return entry && Date.now() - entry.time < PREFETCH_TTL_MS ? entry.request : null;
  }

  /**
   * @param {HTMLAnchorElement} link
   * @returns {{ handle: string, variantId: string | null } | null}
   */
  static #parseProductLink(link) {
    try {
      const url = new URL(link.getAttribute('href') || '', window.location.origin);
      const match = url.pathname.match(/\/products\/([^/?#]+)/);
      if (!match) return null;
      return {
        handle: decodeURIComponent(match[1]),
        variantId: url.searchParams.get('variant'),
      };
    } catch {
      return null;
    }
  }

  /* ------------------------------------------------------------------ */
  /* Panel rendering                                                      */
  /* ------------------------------------------------------------------ */

  #showSkeleton() {
    const { body, skeletonTemplate } = this.#refs;
    if (!body) return;
    body.replaceChildren(skeletonTemplate?.content.cloneNode(true) ?? '');
    this.#refs.badgeSlot?.replaceChildren();
  }

  /**
   * Fetch the panel section for a product / variant and render it.
   * @param {string} handle
   * @param {string | null} variantId
   * @param {{
   *   focus?: string,
   *   quantity?: number,
   *   request?: Promise<HTMLElement> | null,
   *   onRender?: () => void,
   * }} [options] selector to focus after render; quantity to restore in the
   *   stepper; a prefetched panel to use (a fresh fetch replaces it if it
   *   failed); called once the product is on the panel, before the sibling
   *   colours and the pickup line
   */
  async #load(handle, variantId, {
    focus,
    quantity,
    request = null,
    onRender,
  } = {}) {
    const { body } = this.#refs;
    if (!body) return;

    this.#fetchAbort?.abort();
    const controller = new AbortController();
    this.#fetchAbort = controller;
    body.setAttribute('aria-busy', 'true');

    let siblings = null;
    try {
      const fetchFresh = () => this.#fetchSection(handle, variantId, controller.signal);
      const product = await (request ? request.catch(fetchFresh) : fetchFresh());
      if (controller.signal.aborted) return;
      // A product that outlasted the open budget lands on a sliding skeleton.
      await this.#slideDone;
      if (controller.signal.aborted) return;
      siblings = this.#render(product, { focus, quantity });
      onRender?.();
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('[quick-view] could not load the product panel.', error);
      this.#renderError(handle);
    } finally {
      if (this.#fetchAbort === controller) {
        this.#fetchAbort = null;
        body.setAttribute('aria-busy', 'false');
      }
    }
    // Resolves once sibling colours are in, so open() can hold the panel for
    // them too; the form is already usable meanwhile.
    await siblings;
  }

  /**
   * @param {string} handle
   * @param {string | null} variantId
   * @param {AbortSignal} [signal]
   * @returns {Promise<HTMLElement>}
   */
  async #fetchSection(handle, variantId, signal) {
    const root = window.Shopify?.routes?.root || '/';
    const url = new URL(`${root}products/${encodeURIComponent(handle)}`, window.location.origin);
    url.searchParams.set('section_id', this.dataset.section || 'quick-view-product');
    if (variantId) url.searchParams.set('variant', variantId);

    const response = await fetch(url, { signal, headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error(`HTTP ${response.status} for ${url.pathname}`);

    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const product = doc.querySelector(HOOKS.product);
    if (!product) throw new Error('panel markup not found in the section response');
    return product;
  }

  /**
   * @param {HTMLElement} product - the fetched panel root
   * @param {{ focus?: string, quantity?: number }} [options]
   * @returns {Promise<void>} settles when the sibling colours, the pickup
   *   line and the rating are in
   */
  #render(product, { focus, quantity } = {}) {
    const { body, panel } = this.#refs;
    if (!body) return Promise.resolve();

    const wasOpen = Boolean(body.querySelector(HOOKS.product));
    const scrollTop = panel?.scrollTop ?? 0;
    const heightBefore = panel?.offsetHeight ?? 0;

    this.#prepareKlarna(product);
    body.replaceChildren(document.importNode(product, true));
    this.#handle = body.querySelector(HOOKS.product)?.dataset.handle || this.#handle;
    // Every render brings its own badge (it follows the variant); on mobile
    // it shows in the sheet's sticky header.
    this.#refs.badgeSlot?.replaceChildren(...body.querySelectorAll(HOOKS.badge));

    if (panel) panel.scrollTop = scrollTop;
    this.#settleHeight(heightBefore);
    if (quantity) this.#setQuantity(quantity);
    const focusTarget = focus ? body.querySelector(focus) : null;
    focusTarget?.focus({ preventScroll: true });

    this.#mountWishlist();
    const siblings = this.#loadSiblings();
    const pickup = this.#loadPickup();
    const rating = this.#loadRating();

    if (!wasOpen) {
      this.dispatchEvent(new CustomEvent('quick-view:open', {
        bubbles: true,
        detail: { handle: this.#handle },
      }));
    }
    return Promise.all([siblings, pickup, rating]).then(() => {});
  }

  /**
   * The mobile sheet takes its content's height and is anchored to the
   * bottom, so new content of another height moves its top edge. Once the
   * sheet is on screen, play that move instead of jumping (FLIP): start
   * from the old top and slide to the new one. Fixed-size desktop panels
   * measure no change and are left alone.
   * @param {number} heightBefore - panel height before the content changed
   */
  #settleHeight(heightBefore) {
    const { dialog, panel } = this.#refs;
    if (!panel || !dialog?.classList.contains(REVEALED_CLASS) || this.#drag) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const delta = panel.offsetHeight - heightBefore;
    if (Math.abs(delta) < 2 || typeof panel.animate !== 'function') return;
    panel.animate(
      [{ transform: `translateY(${delta}px)` }, { transform: 'translateY(0)' }],
      SETTLE_ANIMATION,
    );
  }

  /** @param {string} handle */
  #renderError(handle) {
    const { body } = this.#refs;
    if (!body) return;
    const root = window.Shopify?.routes?.root || '/';
    const message = document.createElement('div');
    message.className = 'quick-view__error';
    message.setAttribute('role', 'alert');
    message.innerHTML = '<p>Sorry, this product could not be loaded.</p>';
    const link = document.createElement('a');
    link.className = 'quick-view__link';
    link.href = `${root}products/${encodeURIComponent(handle)}`;
    link.textContent = 'View Full Product Details';
    message.append(link);
    body.replaceChildren(message);
    this.#refs.badgeSlot?.replaceChildren();
    link.focus();
  }

  /**
   * Klarna's SDK renders a <klarna-placement> when the element reaches the
   * page and does not retry: one inserted without `data-key` stays empty
   * ("data-key is missing"), and a later refresh() does not bring it back.
   * So the placement is finished on the fetched markup, before it is
   * imported. Without the SDK (or with Klarna off) the wrapper is dropped.
   * @param {HTMLElement} product - fetched panel root, not in the document yet
   */
  #prepareKlarna(product) {
    const wrapper = product.querySelector(HOOKS.klarna);
    if (!wrapper) return;
    const placement = wrapper.querySelector('klarna-placement');
    if (this.hasAttribute('data-klarna-off') || !placement || !window.Klarna?.OnsiteMessaging) {
      wrapper.remove();
      return;
    }
    placement.setAttribute('data-key', this.dataset.klarnaKey || 'credit-promotion-auto-size');
    placement.setAttribute('data-locale', this.dataset.klarnaLocale || 'en-US');
    wrapper.hidden = false;
  }

  /* ------------------------------------------------------------------ */
  /* Options, quantity, siblings                                          */
  /* ------------------------------------------------------------------ */

  /** @param {Event} event */
  #onChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches(HOOKS.option)) return;

    const variant = this.#resolveVariant(input);
    if (!variant || !this.#handle) return;

    // The section re-render is async and the form stays on screen until it
    // lands: point the hidden id at the resolved variant right away, so an
    // add that slips in meanwhile posts the shopper's choice, and hold the
    // button so it cannot race the render (the fresh markup re-enables it).
    const form = input.form || this.#refs.body?.querySelector(HOOKS.form);
    const idInput = form?.querySelector(HOOKS.variantId);
    if (idInput instanceof HTMLInputElement) idInput.value = String(variant.id);
    const add = form?.querySelector(HOOKS.add);
    if (add instanceof HTMLButtonElement) add.disabled = true;

    const escaped = window.CSS?.escape ? CSS.escape(input.value) : input.value.replace(/"/g, '\\"');
    this.#load(this.#handle, String(variant.id), {
      focus: `${HOOKS.option}[data-position="${input.dataset.position}"][value="${escaped}"]`,
      quantity: this.#currentQuantity(),
    });
  }

  /**
   * Variant matching the checked radios; if that combination does not
   * exist, the first variant carrying the value that was just picked.
   * @param {HTMLInputElement} changed
   * @returns {{ id: number, options: string[], available: boolean } | null}
   */
  #resolveVariant(changed) {
    const { body } = this.#refs;
    const script = body?.querySelector(HOOKS.variants);
    if (!script) return null;

    let variants;
    try {
      variants = JSON.parse(script.textContent || '[]');
    } catch {
      return null;
    }

    const checked = [...body.querySelectorAll(`${HOOKS.option}:checked`)]
      .sort((a, b) => Number(a.dataset.position) - Number(b.dataset.position))
      .map((input) => input.value);

    const exact = variants.find((variant) => variant.options.length === checked.length
      && variant.options.every((value, index) => value === checked[index]));
    if (exact) return exact;

    const position = Number(changed.dataset.position) - 1;
    return variants.find((variant) => variant.options[position] === changed.value) || null;
  }

  /** @param {MouseEvent} event */
  #onBodyClick(event) {
    if (!(event.target instanceof Element)) return;

    const step = event.target.closest(HOOKS.qtyStep);
    if (step) {
      this.#stepQuantity(Number(step.getAttribute('data-quick-view-qty-step')) || 0);
      return;
    }

    const sibling = event.target.closest(HOOKS.sibling);
    if (sibling instanceof HTMLElement && sibling.dataset.quickViewSibling) {
      event.preventDefault();
      this.#handle = sibling.dataset.quickViewSibling;
      this.#load(this.#handle, this.#siblingVariantId(this.#handle), {
        focus: HOOKS.title,
        quantity: this.#currentQuantity(),
      });
    }
  }

  /**
   * The sibling's variant that keeps the shopper's non-colour choices (size,
   * amount...), as the PDP does when it navigates to a sibling; null when
   * none matches, which leaves the server to pick the sibling's default.
   * Options are matched by name, not position: siblings may order them
   * differently.
   * @param {string} handle - sibling product handle
   * @returns {string | null} numeric variant id
   */
  #siblingVariantId(handle) {
    const sibling = this.#siblings.find((item) => item.handle === handle);
    const variants = sibling?.variants?.edges?.map(({ node }) => node) || [];
    if (!variants.length) return null;

    const chosen = [...(this.#refs.body?.querySelectorAll(`${HOOKS.option}:checked`) || [])]
      .map((input) => ({
        name: input.closest('[data-quick-view-option-group]')
          ?.getAttribute('data-quick-view-option-group'),
        value: input.value,
      }))
      .filter((entry) => entry.name && !/colou?r/i.test(entry.name));
    if (!chosen.length) return null;

    const match = variants.find((variant) => chosen.every(({ name, value }) => (
      variant.selectedOptions?.some((option) => option.name === name && option.value === value)
    )));
    // Storefront ids are GIDs: gid://shopify/ProductVariant/123
    return match?.id ? String(match.id).split('/').pop() : null;
  }

  /** @returns {number} what the stepper shows, 0 when there is none */
  #currentQuantity() {
    return Number(this.#refs.body?.querySelector(HOOKS.qtyInput)?.value) || 0;
  }

  /**
   * Restore a quantity after a re-render.
   * @param {number} value
   */
  #setQuantity(value) {
    const input = this.#refs.body?.querySelector(HOOKS.qtyInput);
    if (!(input instanceof HTMLInputElement)) return;
    const min = Number(input.min) || 1;
    input.value = String(Math.max(min, value));
  }

  /**
   * Step the quantity, down to the input's min. Purchase limits do not cap
   * the stepper, as on the PDP (lib/product-incrementer.js): the shopper can
   * ask for more, and the add is refused with the PDP's "Limit N of ... per
   * purchase." message (#checkPurchaseLimit), cart contents included.
   * @param {number} delta
   */
  #stepQuantity(delta) {
    const input = this.#refs.body?.querySelector(HOOKS.qtyInput);
    if (!(input instanceof HTMLInputElement)) return;
    const min = Number(input.min) || 1;
    input.value = String(Math.max(min, (Number(input.value) || min) + delta));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Add the sibling products' colors to the panel's color list. Own colors
   * (the `<li data-color>` Liquid rendered) and siblings are sorted together
   * by the family's stitching order (`data-sibling-colors`, the order the PDP
   * sorts by), unknown colors last, so a swatch stays where it was when the
   * shopper picks it and that sibling becomes the loaded product.
   * Liquid already holds the siblings' places (placeholder `<li>`s, CSS
   * `order` on every color), so the swatches replace placeholders of the
   * same size; placeholders whose sibling does not come are dropped.
   */
  async #loadSiblings() {
    const { body } = this.#refs;
    const product = body?.querySelector(HOOKS.product);
    const list = body?.querySelector(HOOKS.siblings);
    const tag = product?.dataset.siblingTag;
    this.#siblings = [];
    if (!product || !list || !tag) return;

    let order = [];
    try {
      order = JSON.parse(product.dataset.siblingColors || '[]');
    } catch {
      order = [];
    }

    const siblings = await this.#fetchSiblings(tag);
    await this.#slideDone;
    // The panel may have moved on while the request was in flight.
    if (!product.isConnected) return;
    this.#siblings = siblings;

    const current = product.dataset.handle;
    const swatches = siblings
      .filter((sibling) => sibling.handle !== current)
      .map((sibling) => {
        const color = QuickView.#siblingColor(sibling);
        return { color, node: QuickView.#siblingSwatch({ ...sibling, color }) };
      });
    const own = [...list.children]
      .filter((node) => node instanceof HTMLElement && node.hasAttribute('data-color')
        && !node.hasAttribute(PLACEHOLDER_ATTR))
      .map((node) => ({ color: node.dataset.color || '', node }));

    if (!swatches.length) {
      const placeholders = list.querySelectorAll(`[${PLACEHOLDER_ATTR}]`);
      if (!placeholders.length) return;
      const heightBefore = this.#refs.panel?.offsetHeight ?? 0;
      placeholders.forEach((node) => node.remove());
      // A group shown only for the siblings has nothing to choose without them.
      const group = list.closest(HOOKS.siblingGroup);
      if (group instanceof HTMLElement && own.length < 2) group.hidden = true;
      this.#settleHeight(heightBefore);
      return;
    }

    const rank = ({ color }) => {
      const index = order.indexOf(color);
      return index === -1 ? order.length : index;
    };
    // Array#sort is stable: equal ranks keep own colors first, then siblings
    // in the order the Storefront API returned them.
    const entries = [...own, ...swatches].sort((a, b) => rank(a) - rank(b));

    const heightBefore = this.#refs.panel?.offsetHeight ?? 0;
    list.replaceChildren(...entries.map(({ node }) => node));
    // The DOM order is the family order now; the CSS order was its stand-in.
    entries.forEach(({ node }) => node.style.removeProperty('order'));
    list.closest(HOOKS.siblingGroup)?.removeAttribute('hidden');
    this.#settleHeight(heightBefore);
  }

  /**
   * @param {string} tag - `Sibling:<id>`
   * @returns {Promise<object[]>}
   */
  #fetchSiblings(tag) {
    if (this.#siblingsCache.has(tag)) return this.#siblingsCache.get(tag);

    const sdgData = window.SDG?.Data;
    const token = sdgData?.publicStorefrontTokenBopis || sdgData?.publicStorefrontToken;
    const shop = window.Shopify?.shop;
    if (!token || !shop) return Promise.resolve([]);

    const [, id = ''] = tag.split(':');
    const request = fetch(`https://${shop}/api/${STOREFRONT_API_VERSION}/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Shopify-Storefront-Access-Token': token,
      },
      body: JSON.stringify({
        query: SIBLINGS_QUERY,
        variables: { query: `tag:Sibling\\:${id}` },
      }),
    })
      .then((response) => response.json())
      .then(({ data, errors }) => {
        if (errors) throw new Error(errors.map((e) => e.message).join('; '));
        return (data?.products?.edges || [])
          .map(({ node }) => node)
          .filter((node) => node.tags?.includes(tag));
      })
      .catch((error) => {
        console.warn('[quick-view] sibling products could not be loaded.', error);
        this.#siblingsCache.delete(tag);
        return [];
      });

    this.#siblingsCache.set(tag, request);
    return request;
  }

  /**
   * @param {object} sibling - Storefront product node
   * @returns {string}
   */
  static #siblingColor(sibling) {
    const option = sibling.variants?.edges?.[0]?.node?.selectedOptions
      ?.find((entry) => /colou?r/i.test(entry.name));
    if (option?.value) return option.value;
    const [, suffix = ''] = (sibling.title || '').split(' - ');
    return suffix.trim();
  }

  /**
   * @param {object} item - sibling with a resolved `color`
   * @returns {HTMLLIElement}
   */
  static #siblingSwatch(item) {
    const li = document.createElement('li');
    li.className = 'quick-view__value';
    li.dataset.available = String(Boolean(item.availableForSale));
    li.dataset.color = item.color || '';

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'quick-view__swatch';
    button.dataset.quickViewSibling = item.handle;
    button.title = item.color;
    button.setAttribute('aria-label', item.color || item.title);

    // The swatch image (alt `<id>_s`) the PDP shows, as in
    // lib/product-configurables.js getSiblingVariantFromOption; products
    // without one fall back to the variant's image.
    const swatch = item.images?.edges
      ?.map(({ node }) => node)
      .find((node) => node?.altText?.split('_').pop() === 's');
    const image = swatch || item.variants?.edges?.[0]?.node?.image || item.featuredImage;
    if (image?.url) {
      const img = document.createElement('img');
      const separator = image.url.includes('?') ? '&' : '?';
      img.src = `${image.url}${separator}width=80`;
      img.alt = '';
      img.loading = 'lazy';
      button.append(img);
    } else {
      const text = document.createElement('span');
      text.className = 'quick-view__sr';
      text.textContent = item.color || item.title;
      button.append(text);
    }

    li.append(button);
    return li;
  }

  /* ------------------------------------------------------------------ */
  /* Wishlist                                                             */
  /* ------------------------------------------------------------------ */

  /**
   * Wishlist King's heart on the gallery, created the way the PDP does it
   * (lib/product-wishlist-button.js): the app renders the button and owns
   * add / remove, and its positioning engine floats it in the gallery's
   * top-right corner, `--quick-view-wishlist-inset` in from both edges (or
   * `--quick-view-wishlist-inset-block` from the top, when set). The
   * gallery wraps the mobile scroller, so the heart stays put while it moves.
   * Every render brings a new gallery, so the heart is created again; before
   * the app is up, the panel on screen gets it on `wishlist-theme-hook-ready`.
   */
  #mountWishlist() {
    const { body } = this.#refs;
    const product = body?.querySelector(HOOKS.product);
    const gallery = body?.querySelector(HOOKS.wishlist);
    if (!(product instanceof HTMLElement) || !(gallery instanceof HTMLElement)) return;

    const theme = window.wishlistKingTheme;
    if (typeof theme?.createComponent !== 'function') {
      if (this.#wishlistPending) return;
      this.#wishlistPending = true;
      window.addEventListener('wishlist-theme-hook-ready', () => {
        this.#wishlistPending = false;
        this.#mountWishlist();
      }, { once: true, signal: this.#abort?.signal });
      return;
    }

    const style = getComputedStyle(gallery);
    const inset = parseFloat(style.getPropertyValue('--quick-view-wishlist-inset')) || 0;
    // From the top edge; the right-edge inset when unset
    const insetBlock = parseFloat(style.getPropertyValue('--quick-view-wishlist-inset-block'));
    gallery.append(theme.createComponent('wishlist-button-product', {
      dataset: {
        productHandle: product.dataset.handle,
        variantId: product.dataset.variantId,
      },
      props: {
        showIcon: true,
        showText: false,
        floating: {
          reference: gallery,
          position: { placement: 'top-end', inset: true },
          offset: { mainAxis: Number.isNaN(insetBlock) ? inset : insetBlock, alignmentAxis: inset },
        },
      },
    }));
  }

  /* ------------------------------------------------------------------ */
  /* Rating                                                               */
  /* ------------------------------------------------------------------ */

  /**
   * Bring the panel's star row in line with the PDP: Liquid rendered it from
   * the Yotpo metafields, which Yotpo fills per product on its own schedule
   * (missing on newer products, stale on others), so ask Yotpo for the
   * numbers its PDP widget shows. No reviews hides the row; a failed request
   * keeps what Liquid rendered, or hides a row that had nothing to show.
   */
  async #loadRating() {
    const row = this.#refs.body?.querySelector(HOOKS.rating);
    if (!(row instanceof HTMLElement)) return;

    const rating = await this.#ratingOf(row);
    await this.#slideDone;
    // The panel may have moved on while the request was in flight.
    if (!row.isConnected) return;

    const heightBefore = this.#refs.panel?.offsetHeight ?? 0;
    if (rating) {
      row.hidden = rating.total < 1;
      if (rating.total > 0) QuickView.#fillRating(row, rating);
    } else if (row.hasAttribute(PENDING_ATTR)) {
      row.hidden = true;
    }
    row.removeAttribute(PENDING_ATTR);
    this.#settleHeight(heightBefore);
  }

  /**
   * @param {HTMLElement} row
   * @param {{ average: number, total: number }} rating
   */
  static #fillRating(row, { average, total }) {
    const stars = row.querySelector(HOOKS.ratingStars);
    if (stars instanceof HTMLElement) {
      const percent = Math.min(100, Math.max(0, average * 20));
      stars.style.setProperty('--quick-view-rating', `${percent}%`);
      stars.setAttribute('aria-label', `Rated ${Math.round(average * 10) / 10} out of 5`);
    }
    const count = row.querySelector(HOOKS.ratingCount);
    if (count) count.textContent = total === 1 ? '1 Review' : `${total} Reviews`;
  }

  /**
   * Average and count from Yotpo's bottomline endpoint, one request per
   * product; failures read as null and are retried on the next render.
   * @param {ParentNode} root - a panel (fetched or on the page) holding the rating row
   * @returns {Promise<{ average: number, total: number } | null>}
   */
  #ratingOf(root) {
    const row = root.matches?.(HOOKS.rating) ? root : root.querySelector(HOOKS.rating);
    const key = row?.getAttribute('data-yotpo-key');
    const id = row?.getAttribute('data-product-id');
    if (!key || !id) return Promise.resolve(null);

    const cached = this.#ratingCache.get(id);
    if (cached) return cached;

    const url = YOTPO_BOTTOMLINE_URL
      .replace('{key}', encodeURIComponent(key))
      .replace('{id}', encodeURIComponent(id));
    const request = fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((data) => {
        const bottomline = data?.response?.bottomline;
        if (!bottomline) throw new Error('no bottomline in the response');
        return {
          average: Number(bottomline.average_score) || 0,
          total: Number(bottomline.total_reviews) || 0,
        };
      })
      .catch((error) => {
        console.warn('[quick-view] Yotpo rating could not be loaded.', error);
        this.#ratingCache.delete(id);
        return null;
      });

    this.#ratingCache.set(id, request);
    return request;
  }

  /* ------------------------------------------------------------------ */
  /* Pickup in store                                                      */
  /* ------------------------------------------------------------------ */

  /** Show the panel's "Pickup in-store" line when the variant is in a store. */
  async #loadPickup() {
    const line = this.#refs.body?.querySelector(HOOKS.pickup);
    if (!(line instanceof HTMLElement)) return;

    const stores = await this.#pickupStores(line);
    await this.#slideDone;
    // The panel may have moved on while the request was in flight.
    if (!line.isConnected) return;

    // The line holds its room, invisible, while the stores load (the sheet
    // keeps its height); no store collapses it.
    const target = line.querySelector(HOOKS.pickupStores);
    if (target) target.textContent = stores.join(' / ');
    const heightBefore = this.#refs.panel?.offsetHeight ?? 0;
    line.hidden = !stores.length;
    line.removeAttribute(PENDING_ATTR);
    this.#settleHeight(heightBefore);
  }

  /**
   * Stores holding the panel's variant, by the PDP's rules
   * (lib/product-bopis.js): ask the inventory API for the enabled stores and
   * the warehouse; a store counts when it has stock, and only when the
   * warehouse has stock too (or the product ships from the vendor). One
   * request per variant; failures read as "no store" and are retried on
   * the next render.
   * @param {ParentNode} root - a panel (fetched or on the page) holding the pickup data
   * @returns {Promise<string[]>} store short titles
   */
  #pickupStores(root) {
    const script = root.querySelector(HOOKS.pickupData);
    let config;
    try {
      config = JSON.parse(script?.textContent || 'null');
    } catch {
      config = null;
    }
    const variantId = config?.variantId ? String(config.variantId) : '';
    const stores = Object.entries(config?.locations?.locations || {})
      .filter(([, location]) => location?.enable)
      .map(([id, location]) => ({ id, name: location.shortTitle }));
    if (!config?.endpoint || !config.key || !variantId || !stores.length) {
      return Promise.resolve([]);
    }

    const cached = this.#pickupCache.get(variantId);
    if (cached) return cached;

    const { warehouseId } = config.locations;
    const locationIds = stores.map(({ id }) => id);
    if (warehouseId) locationIds.push(warehouseId);

    const request = fetch(config.endpoint, {
      method: 'POST',
      headers: { Key: config.key, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        Locations: locationIds,
        Items: [Number(variantId)],
        itemIdentifierType: 'PLU',
        locationIdentifierType: 'Code',
      }),
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then(({ itemQuantities = [] }) => {
        const inStock = new Set();
        itemQuantities.forEach(({ quantities = [] }) => {
          quantities.forEach(({ locationId, available }) => {
            if (available > 0) inStock.add(locationId);
          });
        });
        const warehouseOk = config.shipsFromVendor || (warehouseId && inStock.has(warehouseId));
        if (!warehouseOk) return [];
        return stores
          .filter(({ id }) => inStock.has(id))
          .map(({ name }) => name)
          .filter(Boolean);
      })
      .catch((error) => {
        console.warn('[quick-view] store availability could not be loaded.', error);
        this.#pickupCache.delete(variantId);
        return [];
      });

    this.#pickupCache.set(variantId, request);
    return request;
  }

  /* ------------------------------------------------------------------ */
  /* Add to cart                                                          */
  /* ------------------------------------------------------------------ */

  /** @param {SubmitEvent} event */
  async #onSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches(HOOKS.form)) return;
    event.preventDefault();
    // A variant re-render is in flight: the form on screen is stale.
    if (this.#refs.body?.getAttribute('aria-busy') === 'true') return;

    const button = form.querySelector(HOOKS.add);
    const text = form.querySelector(HOOKS.addText);
    const message = this.#refs.body?.querySelector(HOOKS.message);
    const variantId = form.querySelector(HOOKS.variantId)?.value;
    const quantity = Number(form.querySelector(HOOKS.qtyInput)?.value) || 1;
    const root = window.Shopify?.routes?.root || '/';

    if (message) message.textContent = '';
    if (button instanceof HTMLButtonElement) button.disabled = true;
    const idleText = text?.textContent;
    if (text) text.textContent = 'Adding...';

    try {
      const limitMessage = await this.#checkPurchaseLimit(form, quantity);
      if (limitMessage) throw new Error(limitMessage);

      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(form),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.description || data.message || 'This item could not be added to your cart.');
      }

      if (text) text.textContent = 'Added!';
      await window.updateMiniCart?.();
      // openMiniCart toggles; don't close a cart that is already open.
      const cartOpens = typeof window.openMiniCart === 'function';
      if (cartOpens && !document.body.classList.contains(SCROLL_LOCK_CLASS)) window.openMiniCart();
      // With the cart open, focus belongs there, not on the trigger behind it.
      this.close({ restoreFocus: !cartOpens });

      this.dispatchEvent(new CustomEvent('quick-view:added', {
        bubbles: true,
        detail: { variantId, quantity, item: data },
      }));
    } catch (error) {
      if (message) {
        message.textContent = error.message;
        // Under the sticky buy bar on mobile: scroll it into view
        message.scrollIntoView({ block: 'nearest' });
      }
      if (text && idleText) text.textContent = idleText;
    } finally {
      if (button instanceof HTMLButtonElement) button.disabled = false;
    }
  }

  /**
   * Purchase limits against what is already in the cart, as the PDP does
   * (lib/bag-add-to.js): the cart is fetched on every add; the variant limit
   * is checked against that variant's lines, then the product limit against
   * every line of the product, each with its own message.
   * @param {HTMLFormElement} form
   * @param {number} quantity
   * @returns {Promise<string | null>} the PDP's message, or null when allowed
   */
  async #checkPurchaseLimit(form, quantity) {
    const input = form.querySelector(HOOKS.qtyInput);
    if (!(input instanceof HTMLInputElement)) return null;
    const variantLimit = Number(input.dataset.quickViewVariantLimit) || 0;
    const productLimit = Number(input.dataset.quickViewProductLimit) || 0;
    if (!variantLimit && !productLimit) return null;

    const variantMessage = `Limit ${variantLimit} of "${input.dataset.quickViewVariantTitle || ''}" per purchase.`;
    const productMessage = `Limit ${productLimit} of "${input.dataset.quickViewProductTitle || ''}" per purchase.`;
    if (variantLimit && quantity > variantLimit) return variantMessage;
    if (productLimit && quantity > productLimit) return productMessage;

    const variantId = Number(form.querySelector(HOOKS.variantId)?.value);
    const productId = Number(this.#refs.body?.querySelector(HOOKS.product)?.dataset.productId);
    const root = window.Shopify?.routes?.root || '/';

    let items = [];
    try {
      const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
        .then((response) => response.json());
      items = Array.isArray(cart.items) ? cart.items : [];
    } catch (error) {
      // Fail closed, as the PDP does: without the cart the cap cannot be
      // honoured, so the add is refused rather than allowed unchecked.
      console.warn('[quick-view] cart could not be read for the purchase limit.', error);
      return 'The purchase limit for this item could not be checked. Please try again.';
    }

    const inCart = (predicate) => items
      .filter(predicate)
      .reduce((total, item) => total + item.quantity, 0);
    if (variantLimit && inCart((item) => item.variant_id === variantId) + quantity > variantLimit) {
      return variantMessage;
    }
    if (productLimit && inCart((item) => item.product_id === productId) + quantity > productLimit) {
      return productMessage;
    }
    return null;
  }

  /* ------------------------------------------------------------------ */
  /* Mobile drag to dismiss                                               */
  /* ------------------------------------------------------------------ */

  /**
   * The sheet follows the finger down from its header (transition off while
   * dragging). On release it closes when dragged past DRAG_CLOSE_RATIO of
   * its height or flicked faster than DRAG_CLOSE_VELOCITY, and springs back
   * otherwise; either way the CSS transition runs from where the finger left it.
   * Mobile only, and not from the close button: the pointer capture would
   * take its click.
   * @param {PointerEvent} event
   */
  #onDragStart(event) {
    const { dialog } = this.#refs;
    if (!dialog?.classList.contains(REVEALED_CLASS)) return;
    if (!window.matchMedia(MOBILE_QUERY).matches) return;
    if (event.target instanceof Element && event.target.closest(REFS.close)) return;
    this.#drag = { startY: event.clientY, startTime: event.timeStamp, offset: 0 };
    event.currentTarget?.setPointerCapture?.(event.pointerId);
    dialog.classList.add(DRAGGING_CLASS);
  }

  /** @param {PointerEvent} event */
  #onDragMove(event) {
    const { panel } = this.#refs;
    if (!this.#drag || !panel) return;
    this.#drag.offset = Math.max(0, event.clientY - this.#drag.startY);
    panel.style.transform = `translateY(${this.#drag.offset}px)`;
  }

  /**
   * @param {PointerEvent} event
   * @param {{ cancelled?: boolean }} [options]
   */
  #onDragEnd(event, { cancelled = false } = {}) {
    const drag = this.#drag;
    if (!drag) return;
    this.#drag = null;

    const { dialog, panel } = this.#refs;
    const elapsed = Math.max(1, event.timeStamp - drag.startTime);
    const far = panel ? drag.offset > panel.offsetHeight * DRAG_CLOSE_RATIO : false;
    const flicked = drag.offset > 10 && drag.offset / elapsed > DRAG_CLOSE_VELOCITY;

    dialog?.classList.remove(DRAGGING_CLASS);
    if (!cancelled && (far || flicked)) {
      this.close();
    } else if (panel) {
      panel.style.transform = '';
    }
  }
}

if (!customElements.get('quick-view')) {
  customElements.define('quick-view', QuickView);
}
